import { useCallback, useRef, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MainContext } from "../context/main";
import { fetchPost, fetchPut, fetchDelete, fetchGet } from "../utils";

export default function usePostTags(
  { initialize = true } = { initialize: true }
) {
  const location = useLocation();
  const { mainStore, mainDispatch } = useContext(MainContext);
  const currentPost = useRef(null);
  const isFetching = useRef(false);

  const setTagsFromApi = useCallback(
    (nextTags) => {
      mainDispatch({ type: "MAIN_SET_TAGS", tags: nextTags });
    },
    [mainDispatch]
  );

  const setTagNamesFromApi = useCallback(
    (nextTags) => {
      mainDispatch({ type: "MAIN_SET_TAG_NAMES", tagsNames: nextTags });
    },
    [mainDispatch]
  );

  const setTagMapFromApi = useCallback(
    (nextTags) => {
      mainDispatch({
        type: "MAIN_SET_TAG_MAP",
        tagMap: { ...nextTags, "+": { name: "+" } },
      });
    },
    [mainDispatch]
  );

  const setTagIdMapFromApi = useCallback(
    (nextTags) => {
      mainDispatch({ type: "MAIN_SET_TAG_ID_MAP", tagIdMap: nextTags });
    },
    [mainDispatch]
  );

  const refreshTags = async () => {
    if (isFetching.current) {
      return;
    }
    isFetching.current = true;
    const { body } = await fetchGet({ url: "/api/tags" });
    if (body && body.tags) {
      setTagsFromApi(body.tags);
      setTagNamesFromApi(body.tagNames);
      setTagMapFromApi(body.tagMap);
      setTagIdMapFromApi(body.tagIdMap);
    }
    isFetching.current = false;
  };

  const getNewTagFromUser = async ({ value }) => {
    let tagToReturn = null;

    if (value === "NEW") {
      const nextTag = window.prompt(
        "What would you like to name your new tag?"
      );
      if (nextTag) {
        if (mainStore.tagNames && mainStore.tagNames.includes(nextTag)) {
          window.alert(`Tag "${nextTag}" already exists`);
        } else {
          tagToReturn = nextTag;
        }
      }
    } else if (value === null) {
      window.alert(`Tag name required.`);
    } else {
      tagToReturn = value;
    }
    return tagToReturn;
  };

  const updateTag = async (nextTag) => {
    const { body } = await fetchPut({
      url: "/api/tags",
      data: nextTag,
    });
    if (body) {
      setTagIdMapFromApi({ ...mainStore.tagIdMap, [body._id]: body });
      setTagMapFromApi({ ...mainStore.tagMap, [body.name]: body });
      return body;
    }
  };

  const updateTagsForCurrentPost = async ({ nextTag }) => {
    console.log("updateTagsForCurrentPost");
    const existingTags =
      mainStore.currentPost && mainStore.currentPost.tags
        ? mainStore.currentPost.tags
        : [];
    const { body, error } = await fetchPut({
      data: {
        ...mainStore.currentPost,
        tags: [...existingTags, nextTag],
      },
      onError: console.error,
      url: "/api/wiki",
    });
    await refreshTags();
    return { body, error };
  };

  const updateTagsForFile = async ({ nextTag, file }) => {
    console.log("updateTagsForCurrentPost");
    const existingTags = file && file.tags ? file.tags : [];
    const { body, error } = await fetchPut({
      data: {
        ...file,
        tags: [
          ...existingTags.map((t) => (mainStore.tagIdMap || {})[t].name),
          nextTag,
        ],
      },
      onError: console.error,
      url: "/api/s3",
    });
    await refreshTags();
    return { body, error };
  };

  const updateFile = async ({ file }) => {
    console.log("updateTagsForCurrentPost");
    const { body, error } = await fetchPut({
      data: {
        ...file,
        tags: file.tags.map((t) => (mainStore.tagIdMap || {})[t].name),
      },
      onError: console.error,
      url: "/api/s3",
    });
    await refreshTags();
    return { body, error };
  };

  const addNewTag = async ({ tagName }) => {
    console.log("addNewTag");
    const { body, error } = await fetchPost({
      data: {
        tagName,
      },
      onError: console.error,
      url: "/api/tags",
    });
    await refreshTags();
    return { body, error };
  };

  const deleteTagFromPost = async (tagName) => {
    const { body, error } = await fetchDelete({
      url: "/api/tags/post",
      onError: console.error,
      data: { tagName, postId: mainStore.currentPost.id },
    });
    if (body) {
      await refreshTags();
    }
    return { body, error };
  };

  const deleteTagFromFile = async ({ file, tagName }) => {
    const { body, error } = await fetchDelete({
      url: "/api/tags/file",
      onError: console.error,
      data: { tagName, ...file },
    });
    if (body) {
      await refreshTags();
    }
    return { body, error };
  };

  const deleteTag = async (tagName) => {
    const { body, error } = await fetchDelete({
      url: "/api/tags",
      onError: console.error,
      data: { tagName },
    });
    if (body) {
      await refreshTags();
    }
    return { body, error };
  };

  useEffect(() => {
    if (
      initialize &&
      (mainStore.tags === null || mainStore.tags === undefined)
    ) {
      refreshTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialize, location.pathname]);

  useEffect(() => {
    if (mainStore.currentPost && mainStore.currentPost.tags) {
      currentPost.current = mainStore.currentPost;
      const isNewPost = currentPost.current._id !== mainStore.currentPost._id;
      if (isNewPost) {
        refreshTags();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainStore.currentPost]);

  return {
    tags: mainStore.tags || [],
    tagNames: mainStore.tagNames || [],
    tagMap: mainStore.tagMap || {},
    tagIdMap: mainStore.tagIdMap || {},
    refreshTags,
    updateTagsForCurrentPost,
    deleteTagFromPost,
    deleteTag,
    updateTag,
    addNewTag,
    updateTagsForFile,
    getNewTagFromUser,
    deleteTagFromFile,
    updateFile,
  };
}
